import * as Sentry from '@sentry/react-native';
import 'intl-pluralrules';
import awsExports from '@/aws-exports';
import { AuthProvider } from '@/components/Auth/AuthContext';
import { ConfigurationContextProvider } from '@/components/Contexts/ConfigurationContext';
import { Color } from '@/GlobalStyles';
import useUpdatesChecker from '@/hooks/useUpdatesChecker';
import { DefaultTheme as NavigationDefaultTheme, ThemeProvider } from '@react-navigation/native';
import { Amplify } from 'aws-amplify';
import { I18n } from 'aws-amplify/utils';
import { useFonts } from 'expo-font';
import { Slot, SplashScreen } from 'expo-router';
import { useEffect } from 'react';
import DropDownPicker from 'react-native-dropdown-picker';
import {
  adaptNavigationTheme,
  configureFonts,
  DefaultTheme,
  MD3Theme,
  PaperProvider,
} from 'react-native-paper';

import { fr, registerTranslation } from '@/components/CustomRNPaper/Dates';
registerTranslation('fr', fr);

import '@/language';

import PdfWorker from '@/components/Pdf/PdfWorker';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { ToastProvider } from 'react-native-toast-notifications';
import { GestureHandlerRootView } from 'react-native-gesture-handler';

if (__DEV__) {
  // Adds messages only in a dev environment
  loadDevMessages();
  loadErrorMessages();
}

Sentry.init({
  dsn: 'https://3ee9399427eb354c6fc037f49c3ca57d@o239428.ingest.us.sentry.io/4510190373175296',

  // Adds more context data to events (IP address, cookies, user, etc.)
  // For more information, visit: https://docs.sentry.io/platforms/react-native/data-management/data-collected/
  sendDefaultPii: true,

  // Enable Logs
  enableLogs: true,

  // Configure Session Replay
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1,
  integrations: [Sentry.mobileReplayIntegration()],

  // uncomment the line below to enable Spotlight (https://spotlightjs.com)
  // spotlight: __DEV__,
});

SplashScreen.preventAutoHideAsync();

Amplify.configure({
  ...awsExports,
  Analytics: {
    disabled: true,
  },
});

const fonts = {
  // eslint-disable-next-line global-require
  Roboto_Bold: require('@assets/fonts/Roboto/Roboto-Bold.ttf'),
  // eslint-disable-next-line global-require
  Roboto_Medium: require('@assets/fonts/Roboto/Roboto-Medium.ttf'),
  // eslint-disable-next-line global-require
  Roboto_Regular: require('@assets/fonts/Roboto/Roboto-Regular.ttf'),
  MaterialCommunityIcons: require('react-native-vector-icons/Fonts/MaterialCommunityIcons.ttf'),
};

I18n.setLanguage('fr');
I18n.putVocabularies({
  fr: {
    'An account with the given email already exists.':
      'Un compte avec une adresse e-mail identique existe déjà.',
    'User is disabled.': 'Cet utilisateur est désactivé.',
    'User is not confirmed.': "Cet utilisateur n'est pas vérifié.",
    'Incorrect username or password': "Nom d'utilisateur ou mot de passe incorrect",
    'Password attempts exceeded': 'Tentatives de mot de passe excédées',
  },
});

const fontConfig = {
  customVariant: {
    fontFamily: 'Roboto_Regular',
    fontWeight: '400',
    letterSpacing: 0.5,
    lineHeight: 22,
    fontSize: 20,
  },

  default: {
    fontFamily: 'Roboto_Regular',
    fontWeight: '400',
    letterSpacing: 0,
  },
  displaySmall: {
    fontFamily: 'Roboto_Regular',
    fontSize: 36,
    fontWeight: '400',
    letterSpacing: 0,
    lineHeight: 44,
  },

  displayMedium: {
    fontFamily: 'Roboto_Regular',
    fontSize: 45,
    fontWeight: '400',
    letterSpacing: 0,
    lineHeight: 52,
  },

  displayLarge: {
    fontFamily: 'Roboto_Regular',
    fontSize: 57,
    fontWeight: '400',
    letterSpacing: 0,
    lineHeight: 64,
  },

  headlineSmall: {
    fontFamily: 'Roboto_Regular',
    fontSize: 24,
    fontWeight: '400',
    letterSpacing: 0,
    lineHeight: 32,
  },

  headlineMedium: {
    fontFamily: 'Roboto_Regular',
    fontSize: 28,
    fontWeight: '400',
    letterSpacing: 0,
    lineHeight: 36,
  },

  headlineLarge: {
    fontFamily: 'Roboto_Regular',
    fontSize: 32,
    fontWeight: '400',
    letterSpacing: 0,
    lineHeight: 40,
  },

  titleSmall: {
    fontFamily: 'Roboto_Medium',
    fontSize: 14,
    fontWeight: '500',
    letterSpacing: 0.1,
    lineHeight: 20,
  },

  titleMedium: {
    fontFamily: 'Roboto_Medium',
    fontSize: 16,
    fontWeight: '500',
    letterSpacing: 0.15,
    lineHeight: 24,
  },

  titleLarge: {
    fontFamily: 'Roboto_Medium',
    fontSize: 22,
    fontWeight: '400',
    letterSpacing: 0,
    lineHeight: 28,
  },

  labelSmall: {
    fontFamily: 'Roboto_Regular',
    fontSize: 11,
    fontWeight: '500',
    letterSpacing: 0.5,
    lineHeight: 16,
  },

  labelMedium: {
    fontFamily: 'Roboto_Regular',
    fontSize: 14,
    fontWeight: '600',
    letterSpacing: 0.5,
    lineHeight: 16,
  },

  labelLarge: {
    fontFamily: 'Roboto_Medium',
    fontSize: 15,
    fontWeight: '500',
    letterSpacing: 0.1,
    lineHeight: 20,
  },

  bodySmall: {
    fontFamily: 'Roboto_Regular',
    fontSize: 10,
    fontWeight: '400',
    letterSpacing: 0.4,
    lineHeight: 16,
  },

  bodyMedium: {
    fontFamily: 'Roboto_Regular',
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 0.25,
    lineHeight: 20,
  },

  bodyLarge: {
    fontFamily: 'Roboto_Regular',
    fontSize: 14,
    fontWeight: '400',
    letterSpacing: 0.15,
    lineHeight: 24,
  },
};

const theme: MD3Theme = {
  ...DefaultTheme,

  fonts: configureFonts({ config: fontConfig }),
  roundness: 10,
  colors: {
    ...DefaultTheme.colors,
    background: Color.blanc,
    primary: Color.MainBleu,
    secondary: Color.VertElectrique,
    outline: Color.colorGray_100,
    surfaceVariant: Color.LightMainBleu,
    secondaryContainer: Color.LightMainBleu,
    onPrimaryContainer: Color.MainBleu,
    onSurface: Color.MainNoir,
    onSurfaceVariant: Color.MainNoir,
  },
};

const { LightTheme } = adaptNavigationTheme({
  reactNavigationLight: NavigationDefaultTheme,
  materialLight: theme,
});

const finalTheme = {
  ...LightTheme,
  colors: {
    ...LightTheme.colors,
    card: LightTheme.colors.background,
    border: 'transparent',
  },
};

export const unstable_settings = {
  initialRouteName: 'index',
};

const myTheme = require('@/components/Form/dropDownTheme/mytheme');

DropDownPicker.addTheme('mytheme', myTheme);
DropDownPicker.setTheme('mytheme');
DropDownPicker.setLanguage('FR');

const MainLoggedLayout = () => {
  useUpdatesChecker();
  const [fontsLoaded, fontError] = useFonts(fonts);

  useEffect(() => {
    if (fontsLoaded || fontError) {
      // Hide the splash screen after the fonts have loaded (or an error was returned) and the UI is ready.
      SplashScreen.hideAsync();
    }
  }, [fontsLoaded, fontError]);

  // Prevent rendering until the font has loaded or an error was returned
  if (!fontsLoaded && !fontError) {
    return null;
  }

  // Render the children routes now that all the assets are loaded.
  return (
    <PdfWorker>
      <AuthProvider>
        <PaperProvider theme={theme}>
          <ToastProvider>
            <ConfigurationContextProvider>
              <ThemeProvider value={finalTheme}>
                <GestureHandlerRootView style={{ flex: 1 }}>
                  <Slot />
                </GestureHandlerRootView>
              </ThemeProvider>
            </ConfigurationContextProvider>
          </ToastProvider>
        </PaperProvider>
      </AuthProvider>
    </PdfWorker>
  );
};

export default Sentry.wrap(MainLoggedLayout);
